import { createBrowserRouter, Navigate } from 'react-router-dom';
import App from '../layout/App';
import RestaurantList from '../../features/manager/restaurantList/RestaurantList';
import ContactsPage from '../../features/manager/contacts/ContactsPage';
import KitchenList from '../../features/manager/kitchens/KitchenList';
import MealItemList from '../../features/manager/mealItems/MealItemList';
import { MenuDetails } from '../../features/manager/menus/components/MenuDetails';
import CustomerHomePage from '../../features/customer/home/CustomerHomePage';
import CustomerRestaurantPage from '../../features/customer/restaurant/CustomerRestaurantPage';
import CartPage from '../../features/customer/cart/CartPage';
import Register from '../../features/account/Register';
import Login from '../../features/account/Login';
import ServerError from '../errors/ServerError';

import RequireAuth from './RequireAuth';
import KitchenOrdersPage from '../../features/manager/kitchens/orders/KitchenOrders';
import CustomerOrderPage from '../../features/customer/orders/CustomerOrderPage';
import { UserRoles } from '../utils/userRoles';
import OrderList from '../../features/manager/orders/OrderList';
import RestaurantUserList from '../../features/manager/users/RestaurantUserList';
import KitchenStaffRestaurantList from '../../features/kitchenStaff/restaurantList/KitchenStaffRestaurantList';
import KitchenStaffKitchenList from '../../features/kitchenStaff/kitchens/KitchenStaffKitchenList';
import KitchenStaffMealItemList from '../../features/kitchenStaff/kitchenMealItems/KitchenStaffMealItemList';
import ResetPasswordForm from '../../features/account/ResetPasswordForm';
import VerifyEmail from '../../features/account/verification/VerifyEmail';
import ForgotPasswordForm from '../../features/account/ForgotPasswordForm';
import UserProfile from '../../features/account/profile/UserProfile';
import ChangePassword from '../../features/account/changePassword/ChangePassword';
import HomePage from '../../features/home/HomePage';
import OrderDetails from '../../features/manager/orders/OrderDetails';
import MealItemCustomizationList from '../../features/manager/mealItems/customizations/MealItemCustomizationList';
import CustomerOrderList from '../../features/customer/orders/CustomerOrderList';
import KitchenDetails from '../../features/manager/kitchens/KitchenDetails';
import RestaurantSettings from '../../features/manager/settings/RestaurantSettings';
import RestaurantOpeningHours from '../../features/manager/settings/RestaurantOpeningHours';
import { TableList } from '../../features/manager/tables/components/TableList';
import TaxRateList from '../../features/manager/taxes/TaxRateList';
import { QrCode } from '../../features/manager/tables/components/QrCode';
import { TableFormContainer } from '../../features/manager/tables/components/TableForm/TableFormContainer';
import { MenuList } from '../../features/manager/menus/components/MenuList';
import { MenuForm } from '../../features/manager/menus/components/MenuForm';
import { MenuAvailability } from '../../features/manager/menus/components/MenuAvailability';
import KitchenForm from '../../features/manager/kitchens/KitchenForm';
import MealItemCustomizationDetails from '../../features/manager/mealItems/customizations/MealItemCustomizationDetails';
import MealItemCustomizationForm from '../../features/manager/mealItems/customizations/MealItemCustomizationForm';
import WaiterRestaurantListPage from '../../features/restaurantWaiter/restaurants/WaiterRestaurantListPage';
import { WaiterTableListPage } from '../../features/restaurantWaiter/tables/WaiterTableListPage';
import WaiterOrderListPage from '../../features/restaurantWaiter/orders/WaiterOrderListPage';
import WaiterOrderDetailsPage from '../../features/restaurantWaiter/orders/WaiterOrderDetailsPage';
import WaiterCreateOrderPage from '../../features/restaurantWaiter/orders/WaiterCreateOrderPage';
import WaiterCartPage from '../../features/restaurantWaiter/orders/WaiterCartPage';
import BillerView from '../../features/biller/BillerView';
import AdminPanel from '../../features/admin/AdminPanel';
import Forbidden from '@app/errors/Forbidden';
import NotFound from '@app/errors/NotFound';

// Group restaurant table routes for better organization
const restaurantTableRoutes = [
  {
    path: 'restaurant-tables',
    children: [
      { path: '', element: <TableList /> },
      { path: 'create', element: <TableFormContainer /> },
      { path: ':tableId/edit', element: <TableFormContainer /> },
      { path: 'qr-code', element: <QrCode /> },
    ],
  },
];

const menuRoutes = [
  {
    path: 'menus',
    children: [
      { path: '', element: <MenuList /> },
      { path: 'create', element: <MenuForm /> },
      { path: ':menuId/edit', element: <MenuForm /> },
      { path: ':menuId', element: <MenuDetails /> },
      { path: ':menuId/menu-availability', element: <MenuAvailability /> },
    ],
  },
];

const kitchenRoutes = [
  {
    path: 'kitchens',
    children: [
      { path: '', element: <KitchenList /> },
      { path: 'create', element: <KitchenForm /> },
      { path: ':kitchenId/edit', element: <KitchenForm /> },
      { path: ':kitchenId', element: <KitchenDetails /> },
      { path: ':kitchenId/orders', element: <KitchenOrdersPage /> },
    ],
  },
];

const mealItemCustomizationRoutes = [
  {
    path: 'meal-item-customizations',
    children: [
      { path: '', element: <MealItemCustomizationList /> },
      { path: 'create', element: <MealItemCustomizationForm /> },
      { path: ':customizationId/edit', element: <MealItemCustomizationForm /> },
      { path: ':customizationId', element: <MealItemCustomizationDetails /> },
    ],
  },
];

export const router = createBrowserRouter([
  {
    path: '/',
    element: <App />,
    children: [
      { path: '', element: <HomePage /> },
      { path: '/error/server-error', element: <ServerError /> },
      { path: '/error/not-found', element: <NotFound /> },

      { path: '/forbidden', element: <Forbidden /> },
      { path: '*', element: <Navigate replace to="/error/not-found" /> },
    ],
  },
  {
    path: '/customer',
    element: <App />,
    children: [
      { path: '', element: <CustomerHomePage /> },
      { path: 'restaurants', element: <Navigate replace to="/customer" /> },
      {
        path: 'restaurants/:restaurantId',
        element: <CustomerRestaurantPage />,
      },
      { path: 'cart', element: <CartPage /> },
      {
        path: 'orders',
        element: <CustomerOrderList />,
      },
      {
        path: 'orders/:orderId',
        element: <CustomerOrderPage />,
      },
    ],
  },
  {
    path: '/kitchen-staff',
    element: <App />,
    children: [
      {
        element: (
          <RequireAuth
            roles={[
              UserRoles.SuperAdmin,
              UserRoles.RestaurantManager,
              UserRoles.KitchenStaff,
              UserRoles.RestaurantWaiter,
            ]}
          />
        ),
        children: [
          { path: '', element: <KitchenStaffRestaurantList /> },
          { path: 'restaurants', element: <KitchenStaffRestaurantList /> },
          {
            path: 'restaurants/:restaurantId',
            element: <Navigate to="kitchens" />,
          },
          {
            path: 'restaurants/:restaurantId/kitchens',
            element: <KitchenStaffKitchenList />,
          },
          {
            path: 'restaurants/:restaurantId/kitchens/:kitchenId/orders',
            element: <KitchenOrdersPage />,
          },
          {
            path: 'restaurants/:restaurantId/kitchens/:kitchenId/meal-items',
            element: <KitchenStaffMealItemList />,
          },
          {
            path: 'restaurants/:restaurantId/biller',
            element: <BillerView />,
          },
        ],
      },
    ],
  },
  {
    path: '/manager',
    element: <App />,
    children: [
      {
        element: (
          <RequireAuth
            roles={[UserRoles.SuperAdmin, UserRoles.RestaurantManager]}
          />
        ),
        children: [
          { path: '', element: <RestaurantList /> },
          { path: 'restaurants', element: <RestaurantList /> },
          {
            path: 'restaurants/:restaurantId',
            children: [
              { path: '', element: <Navigate to="orders" /> },
              ...restaurantTableRoutes,
              ...menuRoutes,
              ...kitchenRoutes,
              ...mealItemCustomizationRoutes,
              { path: 'biller', element: <BillerView /> },
            ],
          },
          { path: 'contacts', element: <ContactsPage /> },
          {
            path: 'restaurants/:restaurantId/meal-items',
            element: <MealItemList />,
          },
          { path: 'restaurants/:restaurantId/orders', element: <OrderList /> },
          {
            path: 'restaurants/:restaurantId/orders/:orderId',
            element: <OrderDetails />,
          },
          { path: 'restaurants/:restaurantId/register', element: <Register /> },
          {
            path: 'restaurants/:restaurantId/restaurant-users',
            element: <RestaurantUserList />,
          },
          {
            path: 'restaurants/:restaurantId/settings',
            element: <RestaurantSettings />,
          },
          {
            path: 'restaurants/:restaurantId/settings/opening-hours',
            element: <RestaurantOpeningHours />,
          },
          {
            path: 'restaurants/:restaurantId/taxes',
            element: <TaxRateList />,
          },
        ],
      },
    ],
  },
  {
    path: '/account',
    element: <App />,
    children: [
      { path: 'login', element: <Login /> },
      { path: 'reset-password', element: <ResetPasswordForm /> },
      { path: 'verify-email', element: <VerifyEmail /> },
      { path: 'forgot-password', element: <ForgotPasswordForm /> },
      {
        path: 'profile',
        element: (
          <RequireAuth
            roles={[
              UserRoles.SuperAdmin,
              UserRoles.RestaurantManager,
              UserRoles.KitchenStaff,
              UserRoles.RestaurantWaiter,
            ]}
          />
        ),
        children: [
          { path: 'change-password', element: <ChangePassword /> },
          { path: '', element: <UserProfile /> },
        ],
      },
    ],
  },
  {
    path: '/restaurant-waiter',
    element: <App />,
    children: [
      {
        element: <RequireAuth roles={[UserRoles.RestaurantWaiter]} />,
        children: [
          { path: '', element: <WaiterRestaurantListPage /> },
          {
            path: 'restaurants',
            children: [
              { path: '', element: <WaiterRestaurantListPage /> },
              {
                path: ':restaurantId',
                element: <Navigate to="orders" />,
              },
              {
                path: ':restaurantId/tables',
                element: <WaiterTableListPage />,
              },
              {
                path: ':restaurantId/tables/qr-code',
                element: <QrCode />,
              },
              {
                path: ':restaurantId/orders',
                element: <WaiterOrderListPage />,
              },
              {
                path: ':restaurantId/orders/:orderId',
                element: <WaiterOrderDetailsPage />,
              },
              {
                path: ':restaurantId/orders/create/:cartId',
                element: <WaiterCreateOrderPage />,
              },
              {
                path: ':restaurantId/orders/cart/:cartId',
                element: <WaiterCartPage />,
              },
              {
                path: ':restaurantId/biller',
                element: <BillerView />,
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: '/admin',
    element: <RequireAuth roles={[UserRoles.SuperAdmin]} />,
    children: [{ path: '', element: <AdminPanel /> }],
  },
]);
